import { calculateAge } from '@/modules/people/utils/calculate-age';
import { StatePayDetail } from '@/modules/people/store/employment';
import { JuniorRate } from '@/modules/people/types/employment';
import { JUNIOR_RATE_TYPE } from '@/modules/people/definitions/pay-rates';

/**
 * True when the adult-rate override applies to a pay detail: the contract has a
 * junior row for the employee's age, so the checkbox is shown and a choice can
 * be persisted.
 *
 * Every surface that renders the checkbox and every path that persists the
 * choice must agree on this, or a decision gets saved for an employee who was
 * never offered one.
 */
export function juniorRateOverrideApplies(
  juniorRates: JuniorRate[] | undefined,
  employeeAge: number | null | undefined
): boolean {
  if (!juniorRates?.length || employeeAge == null) {
    return false;
  }
  return juniorRates.some((jr: JuniorRate) => jr.Age === employeeAge);
}

/**
 * Initialize junior rate state on a pay detail.
 * Called from processAgreement, lazy-init, and both pay rate dropdown selection paths.
 *
 * @param payDetail - The pay detail state object to populate
 * @param juniorRates - Junior rates from the contract
 * @param baseRate - The contract's original base pay rule rate (adult rate before any mutation)
 * @param dob - Employee's date of birth string
 * @param juniorRateId - The existing JuniorRateId from the agreement (null for new selections)
 * @param overrideWithAdultRate - The explicit override flag from the agreement.
 *   When absent (undefined/null) the flag is inferred from juniorRateId, preserving
 *   behaviour for stale caches and older API responses that predate the field.
 */
export function initializeJuniorRateState(
  payDetail: Partial<StatePayDetail>,
  {
    juniorRates,
    baseRate,
    dob,
    juniorRateId = undefined,
    overrideWithAdultRate = undefined,
  }: {
    juniorRates: JuniorRate[] | undefined;
    baseRate: number | null | undefined;
    dob: string | null | undefined;
    juniorRateId?: number | null | undefined;
    overrideWithAdultRate?: boolean | null | undefined;
  }
): void {
  payDetail.juniorRates = juniorRates || [];
  payDetail.adultBaseRate = baseRate ?? null;
  payDetail.employeeAge = calculateAge(dob);

  // If juniorRateId was provided from an existing agreement, use it as-is
  // (null means override was active, a number means junior rate was applied).
  // Only look up by age when no juniorRateId was provided (new pay rate selection).
  if (juniorRateId !== undefined) {
    payDetail.juniorRateId = juniorRateId ?? null;
  } else {
    const matchingJr = payDetail.juniorRates.find((jr: JuniorRate) => jr.Age === payDetail.employeeAge);
    payDetail.juniorRateId = matchingJr?.Id ?? null;
  }

  // Where the checkbox is not shown the override is not applicable — keep it
  // null (it must not be persisted as false, nor inferred as true). Where it is
  // shown, prefer the explicit flag from the agreement and only infer it from
  // JuniorRateId (set → not overridden, null → overridden) when it is absent.
  if (!juniorRateOverrideApplies(payDetail.juniorRates, payDetail.employeeAge)) {
    payDetail.overrideWithAdultRate = null;
  } else {
    payDetail.overrideWithAdultRate =
      overrideWithAdultRate == null
        ? payDetail.juniorRateId === null
        : overrideWithAdultRate;
  }
}

/**
 * Resolve the adult rate after a level change.
 *
 * When the user clears the level (selectedLevel is null), the adultBaseRate
 * set by initializeJuniorRateState reflects whichever level was loaded with
 * the employee and is now stale, so we refresh it to the saved "no level"
 * rate. adultRateValue in common-pay-details-library-component reads
 * adultBaseRate directly, so it has to stay in sync.
 */
export function resolveAdultRateOnLevelChange(
  payDetail: Partial<StatePayDetail>,
  selectedLevel: { BaseRate: number } | null | undefined,
  savedNoneLevelSelectedBaseRate: number
): number {
  if (selectedLevel) {
    return selectedLevel.BaseRate;
  }
  payDetail.adultBaseRate = savedNoneLevelSelectedBaseRate;
  return savedNoneLevelSelectedBaseRate;
}

/**
 * Calculate the effective rate based on the adult rate and junior rate override state.
 * Returns the adult rate if override is checked, or the junior rate if unchecked.
 */
export function getEffectiveRate(
  adultRate: number,
  payDetail: Partial<StatePayDetail>
): number {
  if (payDetail.overrideWithAdultRate || !payDetail.juniorRates?.length || payDetail.employeeAge == null) {
    return adultRate;
  }
  const jr = payDetail.juniorRates.find((r: JuniorRate) => r.Age === payDetail.employeeAge);
  if (!jr) {
    return adultRate;
  }
  const value = parseFloat(jr.Value);
  if (Number.isNaN(value)) {
    return adultRate;
  }
  return jr.Type === JUNIOR_RATE_TYPE.MULTIPLIER ? adultRate * value : value;
}
